import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Instagram } from "lucide-react";
import VoroLogo from "./VoroLogo";

export const LegalLayout = ({ title, updated, children, testId }) => {
    return (
        <div
            data-testid={testId}
            className="relative bg-[#050505] text-white min-h-screen overflow-hidden"
        >
            {/* Top nav */}
            <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/[0.06]">
                <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
                    <Link to="/" data-testid="legal-nav-logo" className="flex items-center">
                        <VoroLogo className="h-9" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-nav-back"
                        className="inline-flex items-center gap-1.5 text-[13.5px] text-white/70 hover:text-white transition-colors font-medium"
                    >
                        <ChevronLeft size={16} />
                        Back to home
                    </Link>
                </div>
            </header>

            {/* Aurora background */}
            <div
                aria-hidden
                className="aurora"
                style={{
                    width: 600,
                    height: 600,
                    top: -160,
                    right: -160,
                    background: "#7c3aed",
                    opacity: 0.3,
                }}
            />
            <div
                aria-hidden
                className="aurora"
                style={{
                    width: 500,
                    height: 500,
                    bottom: -200,
                    left: -160,
                    background: "#6d28d9",
                    opacity: 0.2,
                }}
            />

            {/* Content */}
            <main className="relative pt-28 md:pt-36 pb-24 md:pb-32">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-violet-300">
                        Voro · Legal
                    </div>
                    <h1
                        data-testid="legal-title"
                        className="font-display mt-3 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]"
                    >
                        {title}
                    </h1>
                    {updated && (
                        <p className="mt-3 text-white/55 text-sm">Last updated: {updated}</p>
                    )}

                    <div className="legal-prose mt-10 md:mt-12 space-y-7 text-white/75 text-[15.5px] leading-relaxed">
                        {children}
                    </div>
                </div>
            </main>

            {/* Footer (slim) */}
            <footer className="relative border-t border-white/[0.06] py-10">
                <div className="max-w-5xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                    <div className="flex items-center gap-3">
                        <VoroLogo className="h-7" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/55">
                        <Link to="/privacy" className="hover:text-white">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-white">
                            Terms of Service
                        </Link>
                        <Link to="/support" className="hover:text-white">
                            Support
                        </Link>
                        <a href="mailto:hello@voro.au" className="hover:text-white">
                            hello@voro.au
                        </a>
                        <a
                            href="https://www.instagram.com/vorofitness/"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 hover:text-white"
                        >
                            <Instagram size={13} /> Instagram
                        </a>
                    </div>
                    <div className="text-xs text-white/40">
                        © {new Date().getFullYear()} Voro
                    </div>
                </div>
            </footer>
        </div>
    );
};

export const LegalH2 = ({ children, id }) => (
    <h2
        id={id}
        className="font-display text-xl md:text-2xl font-extrabold text-white mt-10 mb-2 tracking-tight"
    >
        {children}
    </h2>
);

export const LegalP = ({ children }) => <p>{children}</p>;

export const LegalUl = ({ children }) => (
    <ul className="space-y-2 list-disc list-outside pl-5 marker:text-violet-400">
        {children}
    </ul>
);

export default LegalLayout;
